import React, { useState, useEffect } from 'react';
import './AdminPanel.css';
import ConfirmModal from './ConfirmModal';
import PromptModal from './PromptModal';
import TemplateManager from './TemplateManager';
import TalonFieldMapping from './TalonFieldMapping';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function AdminPanel({ user, onManageOwner, onImpersonate }) {
  // Super admin only: impersonate ("View as") the owner's real user account.
  const handleViewAs = async (userId) => {
    if (!userId) return;
    try {
      await onImpersonate?.(userId);
    } catch (err) {
      window.alert(err?.message || 'Failed to view as this user');
    }
  };
  const [activeTab, setActiveTab] = useState('owners');
  const [owners, setOwners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportEffective, setSupportEffective] = useState('');
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportMsg, setSupportMsg] = useState('');
  const [acctEmail, setAcctEmail] = useState('');
  const [acctEffective, setAcctEffective] = useState('');
  const [acctSaving, setAcctSaving] = useState(false);
  const [acctMsg, setAcctMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [showEditOwnerModal, setShowEditOwnerModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [newOwner, setNewOwner] = useState({
    email: '',
    username: '',
    password: '',
    name: '',
    phone: '',
    sendInvite: false
  });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, style: 'danger' });
  const [promptModal, setPromptModal] = useState({ isOpen: false, title: '', message: '', placeholder: '', onSubmit: null });
  // Multi-step prompt was simplified; no longer used
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', message: '' });
  const [dealershipActionId, setDealershipActionId] = useState(null); // limit button disable to a single dealership
  const [dealershipFormModal, setDealershipFormModal] = useState({ isOpen: false, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75 });
  const [copyModal, setCopyModal] = useState({ isOpen: false, dealership: null, targetOwnerId: '', newName: '', move: false });
  const [betaSaving, setBetaSaving] = useState({}); // { [dealershipId]: boolean }
  const [talonSync, setTalonSync] = useState({}); // { [dealershipId]: { loading, result, error } }
  const [templates, setTemplates] = useState([]); // dropdown templates available when creating a dealership

  // Only the super admin (role ADMIN) sees staff management, add-on toggles,
  // and destructive (copy/delete) actions. Site managers manage owners/dealerships.
  const isSuperAdmin = user?.role === 'ADMIN';
  // Platform managers (admin + site manager) can manage dropdown templates.
  const isPlatformManager = user?.role === 'ADMIN' || user?.role === 'SITE_MANAGER';
  const [staffUsers, setStaffUsers] = useState([]);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    email: '', username: '', password: '', name: '', phone: '', role: 'SITE_MANAGER', sendInvite: false
  });

  useEffect(() => {
    if (activeTab === 'owners') {
      fetchOwners();
    } else if (activeTab === 'dashboard') {
      fetchStats();
    } else if (activeTab === 'beta') {
      fetchOwners();
    } else if (activeTab === 'talonFields') {
      fetchOwners();
    } else if (activeTab === 'admins') {
      fetchStaff();
    } else if (activeTab === 'support') {
      fetchSupportEmail();
      fetchAccountRequestEmail();
    }
  }, [activeTab]);

  // Load dropdown templates once so they're ready in the create-dealership picker.
  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchSupportEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/support-email`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch support email');
      const data = await response.json();
      setSupportEmail(data.supportEmail || '');
      setSupportEffective(data.effectiveEmail || '');
    } catch (err) {
      console.error('Failed to fetch support email', err);
    }
  };

  const saveSupportEmail = async () => {
    setSupportSaving(true);
    setSupportMsg('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/support-email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ supportEmail })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSupportMsg(data.error || 'Failed to save.');
        return;
      }
      setSupportEffective(data.supportEmail || supportEmail);
      setSupportMsg('Saved.');
    } catch (err) {
      setSupportMsg('Failed to save.');
    } finally {
      setSupportSaving(false);
    }
  };

  const fetchAccountRequestEmail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/account-request-email`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch account-request email');
      const data = await response.json();
      setAcctEmail(data.accountRequestEmail || '');
      setAcctEffective(data.effectiveEmail || '');
    } catch (err) {
      console.error('Failed to fetch account-request email', err);
    }
  };

  const saveAccountRequestEmail = async () => {
    setAcctSaving(true);
    setAcctMsg('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/account-request-email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accountRequestEmail: acctEmail })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAcctMsg(data.error || 'Failed to save.');
        return;
      }
      setAcctEffective(data.accountRequestEmail || acctEmail);
      setAcctMsg('Saved.');
    } catch (err) {
      setAcctMsg('Failed to save.');
    } finally {
      setAcctSaving(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return; // non-fatal: picker falls back to default-on-create
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch dropdown templates', err);
    }
  };

  // Default template pre-selected in the create-dealership picker.
  const defaultTemplateId = templates.find(t => t.name === 'Default')?.id || '';

  const fetchOwners = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/owners`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch owners');
      }

      const data = await response.json();
      setOwners(data.owners);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/staff`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setStaffUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/staff`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newStaff)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create user');
      }
      setShowAddStaffModal(false);
      setNewStaff({ email: '', username: '', password: '', name: '', phone: '', role: 'SITE_MANAGER', sendInvite: false });
      fetchStaff();
      setInfoModal({ isOpen: true, title: 'User Created', message: result.message || 'The account has been created successfully.' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendStaffInvite = (staff) => {
    const doSend = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/staff/${staff.id}/send-invite`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to send invitation');
        }
        fetchStaff();
        setInfoModal({ isOpen: true, title: 'Invitation Sent', message: data.message || 'The setup invitation has been emailed.' });
      } catch (err) {
        setError(err.message);
        setInfoModal({ isOpen: true, title: 'Invitation Failed', message: err.message });
      } finally {
        setLoading(false);
      }
    };

    const hasLoggedIn = !!staff.lastLogin;
    if (hasLoggedIn && !staff.invitePending) {
      setConfirmModal({
        isOpen: true,
        title: 'Resend Invitation?',
        message: `${staff.name} has already activated their account. Sending a new invitation will reset their password—they won't be able to log in until they set a new one via the emailed link. Continue?`,
        confirmText: 'Send & Reset',
        style: 'danger',
        onConfirm: doSend
      });
    } else {
      doSend();
    }
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/admin/owners`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newOwner)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create owner');
      }

      // Success
      setShowAddOwnerModal(false);
      const result = await response.json().catch(() => ({}));
      setNewOwner({
        email: '',
        username: '',
        password: '',
        name: '',
        phone: '',
        sendInvite: false
      });
      fetchOwners();
      setInfoModal({
        isOpen: true,
        title: 'Owner Created',
        message: result.message || 'The owner account has been created successfully.'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOwnerInvite = (owner) => {
    const doSend = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/owners/${owner.id}/send-invite`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Failed to send invitation');
        }
        fetchOwners();
        setInfoModal({
          isOpen: true,
          title: 'Invitation Sent',
          message: data.message || 'The setup invitation has been emailed to the owner.'
        });
      } catch (err) {
        setError(err.message);
        setInfoModal({ isOpen: true, title: 'Invitation Failed', message: err.message });
      } finally {
        setLoading(false);
      }
    };

    const hasLoggedIn = !!owner.user?.lastLogin;
    const invitePending = owner.user?.invitePending;
    // Only the destructive case (already-activated owner) needs a confirmation.
    if (hasLoggedIn && !invitePending) {
      setConfirmModal({
        isOpen: true,
        title: 'Resend Invitation?',
        message: `${owner.name} has already activated their account. Sending a new invitation will reset their password—they won't be able to log in until they set a new one via the emailed link. Continue?`,
        confirmText: 'Send & Reset',
        style: 'danger',
        onConfirm: doSend
      });
    } else {
      doSend();
    }
  };

  const handleEditOwner = (owner) => {
    setEditingOwner({
      id: owner.id,
      name: owner.name,
      phone: owner.phone || '',
      email: owner.email,
      dealerships: owner.dealerships || []
    });
    setShowEditOwnerModal(true);
  };

  const handleUpdateOwner = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/admin/owners/${editingOwner.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingOwner.name,
          phone: editingOwner.phone,
          email: editingOwner.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update owner');
      }

      setShowEditOwnerModal(false);
      setEditingOwner(null);
      fetchOwners();
      setInfoModal({
        isOpen: true,
        title: 'Owner Updated',
        message: 'Owner details have been saved successfully.'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateOwner = (ownerId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Deactivate Owner',
      message: 'Are you sure you want to deactivate this owner? This will deactivate all their dealerships and employees.',
      confirmText: 'Deactivate',
      style: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          setError('');
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}/api/admin/owners/${ownerId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to deactivate owner');
          }

          fetchOwners();
          setInfoModal({
            isOpen: true,
            title: 'Owner Deactivated',
            message: 'The owner and all related dealerships/employees have been deactivated.'
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleActivateOwner = async (ownerId) => {
    // For now, we'll use the update endpoint to reactivate
    // You may want to add a specific reactivate endpoint in the backend
    setInfoModal({ isOpen: true, title: 'Coming Soon', message: 'Reactivation feature coming soon. Please contact support to reactivate an owner.' });
  };

  const handleAddDealershipToOwner = (dealership = null) => {
    if (dealership) {
      // Edit mode
      setDealershipFormModal({ isOpen: true, editingId: dealership.id, name: dealership.name, location: dealership.location, demoMode: dealership.demoMode || false, achievementRate: dealership.achievementRate || 0.75 });
    } else {
      // Add mode — pre-select the Default dropdown template.
      setDealershipFormModal({ isOpen: true, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75, templateId: defaultTemplateId });
    }
  };

  const handleSaveDealership = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const { name, location, editingId, demoMode, achievementRate } = dealershipFormModal;
      console.log(editingId ? '🟠 Updating dealership' : '🟠 Creating dealership', { name, location, ownerId: editingOwner?.id });

      if (editingId) {
        // Update existing dealership
        const response = await fetch(`${API_URL}/api/admin/dealerships/${editingId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name, location, demoMode, achievementRate })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Update dealership failed', { status: response.status, errorData });
          throw new Error(errorData.error || 'Failed to update dealership');
        }

        const result = await response.json();
        const updatedDealership = result.dealership || result;
        console.log('✅ Update dealership success', updatedDealership);
        
        setEditingOwner(prev => ({
          ...prev,
          dealerships: prev.dealerships.map(d => d.id === editingId ? updatedDealership : d)
        }));

        setInfoModal({
          isOpen: true,
          title: 'Dealership Updated',
          message: 'The dealership has been updated successfully.'
        });
      } else {
        // Create new dealership
        const response = await fetch(`${API_URL}/api/admin/dealerships`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name,
            location,
            ownerId: editingOwner.id,
            // '' from the picker means "None" → null tells the backend to start blank.
            templateId: dealershipFormModal.templateId ? dealershipFormModal.templateId : null
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ Add dealership failed', { status: response.status, errorData });
          throw new Error(errorData.error || 'Failed to add dealership');
        }

        const result = await response.json();
        const newDealership = result.dealership || result;
        console.log('✅ Add dealership success', newDealership);
        
        setEditingOwner(prev => ({
          ...prev,
          dealerships: [...(prev?.dealerships || []), newDealership]
        }));

        setInfoModal({
          isOpen: true,
          title: 'Dealership Added',
          message: 'The dealership has been created and linked to the owner.'
        });
      }

      fetchOwners();
      setDealershipFormModal({ isOpen: false, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75 });
    } catch (err) {
      console.error('❌ Dealership error', err);
      setError(err.message);
      setInfoModal({
        isOpen: true,
        title: 'Failed to Save Dealership',
        message: err.message || 'Unable to save the dealership.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDealershipStatus = (dealershipId, currentStatus) => {
    const action = currentStatus === 'ACTIVE' ? 'deactivate' : 'activate';
    const actionPast = currentStatus === 'ACTIVE' ? 'deactivated' : 'activated';
    
    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Dealership`,
      message: `Are you sure you want to ${action} this dealership? This will affect all employees and data access.`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      style: currentStatus === 'ACTIVE' ? 'danger' : 'success',
      onConfirm: async () => {
        try {
          setError('');
          setDealershipActionId(dealershipId);
          const token = localStorage.getItem('token');
          const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

          const response = await fetch(`${API_URL}/api/admin/dealerships/${dealershipId}/status`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              status: newStatus
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to ${action} dealership`);
          }

          // Update the editing owner's dealerships array
          setEditingOwner({
            ...editingOwner,
            dealerships: editingOwner.dealerships.map(d =>
              d.id === dealershipId ? { ...d, status: newStatus } : d
            )
          });

          // Refresh owners list
          fetchOwners();
          setInfoModal({
            isOpen: true,
            title: `Dealership ${actionPast.charAt(0).toUpperCase() + actionPast.slice(1)}`,
            message: `The dealership has been ${actionPast}.`
          });
        } catch (err) {
          console.error('❌ Toggle dealership status error', err);
          setInfoModal({
            isOpen: true,
            title: `Failed to ${action.charAt(0).toUpperCase() + action.slice(1)} Dealership`,
            message: err.message || `Unable to ${action} dealership.`
          });
        } finally {
          setDealershipActionId(null);
        }
      }
    });
  };

  const handleDeleteDealership = (dealershipId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Dealership',
      message: 'This will permanently delete the dealership and all associated data. This action cannot be undone. Continue?',
      confirmText: 'Delete',
      style: 'danger',
      onConfirm: async () => {
        try {
          setError('');
          setDealershipActionId(dealershipId);
          const token = localStorage.getItem('token');

          const response = await fetch(`${API_URL}/api/admin/dealerships/${dealershipId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to delete dealership');
          }

          // Remove dealership from editing owner state
          setEditingOwner({
            ...editingOwner,
            dealerships: editingOwner.dealerships.filter(d => d.id !== dealershipId)
          });

          fetchOwners();
          setInfoModal({
            isOpen: true,
            title: 'Dealership Deleted',
            message: 'The dealership has been permanently removed.'
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setDealershipActionId(null);
        }
      }
    });
  };

  const handleCopyDealership = async () => {
    const { dealership, targetOwnerId, newName, move } = copyModal;
    if (!dealership || !targetOwnerId) return;
    if (!move && !newName.trim()) return;

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/admin/dealerships/${dealership.id}/copy`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetOwnerId, newName, move })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to ${move ? 'move' : 'copy'} dealership`);
      }

      setCopyModal({ isOpen: false, dealership: null, targetOwnerId: '', newName: '', move: false });

      if (move) {
        setEditingOwner(prev => ({
          ...prev,
          dealerships: prev.dealerships.filter(d => d.id !== dealership.id)
        }));
      }

      await fetchOwners();
      setInfoModal({
        isOpen: true,
        title: move ? 'Dealership Moved' : 'Dealership Copied',
        message: move
          ? `${dealership.name} has been moved to the selected owner.`
          : `A copy of ${dealership.name} named "${newName}" has been created.`
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBetaFeature = async (dealership, featureKey, currentValue) => {
    const newFeatures = { ...(dealership.betaFeatures || {}), [featureKey]: !currentValue };
    setBetaSaving(prev => ({ ...prev, [dealership.id]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dealerships/${dealership.id}/beta-features`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ betaFeatures: newFeatures })
      });
      if (!response.ok) throw new Error('Failed to update beta features');
      // Update owners state in-place
      setOwners(prev => prev.map(owner => ({
        ...owner,
        dealerships: owner.dealerships.map(d =>
          d.id === dealership.id ? { ...d, betaFeatures: newFeatures } : d
        )
      })));
    } catch (err) {
      setError(err.message);
    } finally {
      setBetaSaving(prev => ({ ...prev, [dealership.id]: false }));
    }
  };

  const handleTalonSync = async (dealershipId) => {
    setTalonSync(prev => ({ ...prev, [dealershipId]: { loading: true, result: null, error: null } }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/talon/sync/${dealershipId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sync failed');
      setTalonSync(prev => ({ ...prev, [dealershipId]: { loading: false, result: data.result, error: null } }));
    } catch (err) {
      setTalonSync(prev => ({ ...prev, [dealershipId]: { loading: false, result: null, error: err.message } }));
    }
  };

  const filteredOwners = owners.filter(owner => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      owner.name?.toLowerCase().includes(search) ||
      owner.email?.toLowerCase().includes(search) ||
      owner.user?.username?.toLowerCase().includes(search)
    );
  });

  // One source for both the tab bar and the narrow-width picker below, so the
  // two can't drift apart as tabs are added or their permissions change.
  const adminTabs = [
    { id: 'dashboard', label: 'Dashboard', visible: true },
    { id: 'owners', label: 'Dealership Owners', visible: true },
    { id: 'admins', label: 'Admins', visible: isSuperAdmin },
    { id: 'beta', label: 'Beta Features', visible: isSuperAdmin },
    { id: 'talonFields', label: 'TALON Fields', visible: isSuperAdmin },
    { id: 'templates', label: 'Dropdown Templates', visible: isPlatformManager },
    { id: 'support', label: 'Support', visible: isSuperAdmin },
  ].filter(tab => tab.visible);

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>System Administration</p>
      </div>

      {/* Below the width where the full row fits, the tabs are replaced by this
          picker rather than wrapped onto ragged second and third rows. */}
      <div className="admin-tab-picker">
        <label htmlFor="admin-tab-select">Section</label>
        <select
          id="admin-tab-select"
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
        >
          {adminTabs.map(tab => (
            <option key={tab.id} value={tab.id}>{tab.label}</option>
          ))}
        </select>
      </div>

      <div className="admin-tabs">
        {adminTabs.map(tab => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {activeTab === 'dashboard' && (
        <div className="admin-dashboard">
          {loading ? (
            <div className="loading">Loading statistics...</div>
          ) : stats ? (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Owners</div>
                <div className="stat-value">{stats.totalOwners}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Dealerships</div>
                <div className="stat-value">{stats.totalDealerships}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active Dealerships</div>
                <div className="stat-value">{stats.activeDealerships}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Inactive Dealerships</div>
                <div className="stat-value">{stats.inactiveDealerships}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{stats.totalUsers}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active Users (30d)</div>
                <div className="stat-value">{stats.activeUsers}</div>
              </div>
            </div>
          ) : (
            <div>No statistics available</div>
          )}
        </div>
      )}

      {activeTab === 'admins' && isSuperAdmin && (
        <div className="admin-owners">
          <div className="owners-controls">
            <div />
            <button className="btn-add-owner" onClick={() => setShowAddStaffModal(true)}>
              + Add Admin / Site Manager
            </button>
          </div>
          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <div className="owners-table-wrapper">
              <table className="owners-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staffUsers.map(staff => (
                    <tr key={staff.id}>
                      <td data-label="Name">{staff.name}</td>
                      <td data-label="Email">{staff.email}</td>
                      <td data-label="Role">{staff.role === 'ADMIN' ? 'Admin' : 'Site Manager'}</td>
                      <td data-label="Last Login">{staff.lastLogin ? new Date(staff.lastLogin).toLocaleDateString() : 'Never'}</td>
                      <td data-label="Status">
                        <span className={`status-badge ${staff.invitePending ? 'status-inactive' : 'status-active'}`}>
                          {staff.invitePending ? 'Invite pending' : 'Active'}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleSendStaffInvite(staff)}
                            disabled={loading}
                            title={staff.invitePending ? 'Resend setup invitation' : 'Send setup invitation'}
                          >
                            {staff.invitePending ? 'Resend Invite' : 'Send Invite'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'owners' && (
        <div className="admin-owners">
          <div className="owners-controls">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search owners by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>
            <button 
              className="btn-add-owner"
              onClick={() => setShowAddOwnerModal(true)}
            >
              + Add New Owner
            </button>
          </div>

          {loading ? (
            <div className="loading">Loading owners...</div>
          ) : filteredOwners.length === 0 ? (
            <div className="no-results">
              <p>{searchTerm ? 'No owners found matching your search.' : 'No owners found.'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="owners-table">
                <thead>
                  <tr>
                    <th>Owner Name</th>
                    <th>Email</th>
                    <th>Dealerships</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOwners.map(owner => {
                    const isActive = owner.dealerships?.some(d => d.status === 'ACTIVE') || owner.dealershipCount === 0;
                    return (
                      <tr key={owner.id}>
                        <td data-label="Owner">{owner.name}</td>
                        <td data-label="Email">{owner.email}</td>
                        <td data-label="Dealerships">
                          {owner.dealershipCount > 0 ? (
                            <span className="dealership-count">
                              {owner.dealershipCount} dealership{owner.dealershipCount !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="no-dealerships">No dealerships</span>
                          )}
                        </td>
                        <td data-label="Last Login">
                          {owner.user?.lastLogin 
                            ? new Date(owner.user.lastLogin).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td data-label="Status">
                          <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td data-label="Actions">
                          <div className="action-buttons">
                            <button
                              className="btn-manage"
                              onClick={() => onManageOwner?.(owner)}
                              disabled={loading}
                              title="Manage this owner's data"
                            >
                              Manage
                            </button>
                            {isSuperAdmin && owner.userId && (
                              <button
                                className="btn-manage"
                                onClick={() => handleViewAs(owner.userId)}
                                disabled={loading}
                                title="Impersonate this owner (see exactly what they see)"
                              >
                                View as
                              </button>
                            )}
                            <button 
                              className="btn-edit"
                              onClick={() => handleEditOwner(owner)}
                              disabled={loading}
                              title="Edit owner"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                              Edit
                            </button>
                            <button
                              className="btn-edit"
                              onClick={() => handleSendOwnerInvite(owner)}
                              disabled={loading}
                              title={owner.user?.invitePending ? 'Resend setup invitation' : 'Send setup invitation'}
                            >
                              {owner.user?.invitePending ? 'Resend Invite' : 'Send Invite'}
                            </button>
                            {isActive ? (
                              <button 
                                className="btn-deactivate"
                                onClick={() => handleDeactivateOwner(owner.id)}
                                disabled={loading}
                                title="Deactivate owner"
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button 
                                className="btn-activate"
                                onClick={() => handleActivateOwner(owner.id)}
                                disabled={loading}
                                title="Activate owner"
                              >
                                Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'beta' && (
        <div className="admin-owners">
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
              Manage add-on integrations and beta features per dealership. Add-ons require additional configuration by the dealership owner. Beta features are experimental and hidden by default.
            </p>
          </div>

          {loading ? (
            <div className="loading">Loading dealerships...</div>
          ) : owners.length === 0 ? (
            <div className="no-results"><p>No owners found.</p></div>
          ) : (
            owners.map(owner => (
              owner.dealerships && owner.dealerships.length > 0 && (
                <div key={owner.id} style={{ marginBottom: '32px' }}>
                  <div style={{ fontWeight: '600', fontSize: '15px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    {owner.name}
                    <span style={{ marginLeft: '10px', fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: '400' }}>{owner.email}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {owner.dealerships.map(dealership => {
                      const features = dealership.betaFeatures || {};
                      const isSaving = betaSaving[dealership.id];

                      const ADD_ONS = [
                        { key: 'talonApi', label: 'TALON API', description: 'Sync deals, inventory & repair orders from TALON DMS' },
                      ];

                      const BETA_FEATURES = [
                        { key: 'insights', label: 'Insights', description: 'Analytics & insights page' },
                        { key: 'visualSalesDashboard', label: 'Visual Sales Dashboard', description: 'Visual sales scoreboard display' },
                        { key: 'salesScoreboard', label: 'Sales Scoreboard', description: 'Sales scoreboard display' },
                      ];

                      const renderFeatureCard = (feature) => {
                        const enabled = !!features[feature.key];
                        const sync = talonSync[dealership.id] || {};
                        return (
                          <div key={feature.key} style={{ borderRadius: '8px', background: enabled ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${enabled ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.07)'}`, transition: 'all 0.15s', overflow: 'hidden' }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: isSaving ? 'wait' : 'pointer', padding: '10px 12px' }}>
                              <div style={{ position: 'relative', marginTop: '1px' }}>
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  disabled={isSaving}
                                  onChange={() => handleToggleBetaFeature(dealership, feature.key, enabled)}
                                  style={{ width: '16px', height: '16px', cursor: isSaving ? 'wait' : 'pointer', accentColor: '#10b981', flexShrink: 0 }}
                                />
                              </div>
                              <div>
                                <div style={{ fontWeight: '500', fontSize: '13px', color: enabled ? 'rgba(16,185,129,1)' : 'rgba(255,255,255,0.8)' }}>{feature.label}</div>
                                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{feature.description}</div>
                              </div>
                            </label>
                            {feature.key === 'talonApi' && enabled && (
                              <div style={{ padding: '0 12px 10px 38px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                <div style={{ fontSize: '11px', color: dealership.talonDatasetKey ? 'rgba(16,185,129,0.8)' : 'rgba(251,191,36,0.8)', margin: '8px 0 6px' }}>
                                  {dealership.talonDatasetKey ? `Dataset Key: ${dealership.talonDatasetKey.slice(0, 8)}…` : 'Dataset key not set — owner must configure'}
                                </div>
                                {dealership.talonDatasetKey && (
                                  <div>
                                    <button
                                      onClick={() => handleTalonSync(dealership.id)}
                                      disabled={sync.loading}
                                      style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '5px', border: '1px solid rgba(16,185,129,0.4)', background: sync.loading ? 'rgba(255,255,255,0.05)' : 'rgba(16,185,129,0.12)', color: 'rgba(16,185,129,0.9)', cursor: sync.loading ? 'wait' : 'pointer' }}
                                    >
                                      {sync.loading ? 'Syncing…' : 'Sync Now'}
                                    </button>
                                    {sync.result && (
                                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                                        Deals: {sync.result.deals.synced} · Inventory: {sync.result.inventory.synced} · Service: {sync.result.service.synced}
                                        {(sync.result.deals.error || sync.result.inventory.error || sync.result.service.error) && (
                                          <span style={{ color: 'rgba(239,68,68,0.8)', marginLeft: '4px' }}>
                                            {sync.result.deals.error || sync.result.inventory.error || sync.result.service.error}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {sync.error && (
                                      <div style={{ fontSize: '11px', color: 'rgba(239,68,68,0.8)', marginTop: '4px' }}>{sync.error}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return (
                        <div key={dealership.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '14px' }}>
                            <div>
                              <span style={{ fontWeight: '500', color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>{dealership.name}</span>
                              {dealership.location && (
                                <span style={{ marginLeft: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{dealership.location}</span>
                              )}
                            </div>
                            <span className={`status-badge ${dealership.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`} style={{ fontSize: '11px', flexShrink: 0 }}>
                              {dealership.status}
                            </span>
                          </div>

                          {/* Add-ons */}
                          <div style={{ marginBottom: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>Add-ons</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                              {ADD_ONS.map(renderFeatureCard)}
                            </div>
                          </div>

                          {/* Beta Features */}
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>Beta Features</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px' }}>
                              {BETA_FEATURES.map(renderFeatureCard)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      )}

      {showAddOwnerModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Franchise Owner</h2>
              <button className="close-btn" onClick={() => setShowAddOwnerModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddOwner}>
              <div className="form-section">
                <h3>Account Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newOwner.email}
                      onChange={(e) => setNewOwner({...newOwner, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={newOwner.username}
                      onChange={(e) => setNewOwner({...newOwner, username: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={newOwner.password}
                      onChange={(e) => setNewOwner({...newOwner, password: e.target.value})}
                      minLength={8}
                      placeholder="Leave blank to invite"
                      autoComplete="new-password"
                    />
                    <small style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
                      Leave blank and the owner sets their own password via the setup link.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={newOwner.name}
                      onChange={(e) => setNewOwner({...newOwner, name: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newOwner.phone}
                    onChange={(e) => setNewOwner({...newOwner, phone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newOwner.sendInvite}
                      onChange={(e) => setNewOwner({...newOwner, sendInvite: e.target.checked})}
                      style={{ width: 'auto' }}
                    />
                    Send setup invitation email now
                  </label>
                  <small style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
                    Leave unchecked if the dealership isn’t configured yet—you can send the invite later from the owners list.
                  </small>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowAddOwnerModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Owner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddStaffModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Admin / Site Manager</h2>
              <button className="close-btn" onClick={() => setShowAddStaffModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddStaff}>
              <div className="form-section">
                <h3>Account Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={newStaff.role}
                      onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                      required
                    >
                      <option value="SITE_MANAGER">Site Manager</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                    <small style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
                      Site Managers can manage owners & dealerships. Admins have full access.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={newStaff.name}
                      onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newStaff.email}
                      onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={newStaff.username}
                      onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={newStaff.password}
                      onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                      minLength={8}
                      placeholder="Leave blank to invite"
                      autoComplete="new-password"
                    />
                    <small style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>
                      Leave blank and they set their own password via the setup link.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={newStaff.phone}
                      onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newStaff.sendInvite}
                      onChange={(e) => setNewStaff({ ...newStaff, sendInvite: e.target.checked })}
                      style={{ width: 'auto' }}
                    />
                    Send setup invitation email now
                  </label>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddStaffModal(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditOwnerModal && editingOwner && (
        <div className="modal-overlay">
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Owner - {editingOwner.name}</h2>
              <button className="close-btn" onClick={() => setShowEditOwnerModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateOwner}>
              <div className="form-section">
                <h3>Owner Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={editingOwner.email}
                      onChange={(e) => setEditingOwner({...editingOwner, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={editingOwner.phone}
                      onChange={(e) => setEditingOwner({...editingOwner, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={editingOwner.name}
                    onChange={(e) => setEditingOwner({...editingOwner, name: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
                  <h3 style={{margin: 0}}>Dealerships</h3>
                  <button 
                    type="button"
                    className="btn-add-dealership-inline"
                    onClick={handleAddDealershipToOwner}
                    disabled={loading}
                  >
                    + Add Dealership
                  </button>
                </div>

                {editingOwner.dealerships && editingOwner.dealerships.length > 0 ? (
                  <div className="table-scroll">
                  <table className="dealerships-table-inline">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editingOwner.dealerships.map(dealership => (
                        <tr key={dealership.id}>
                          <td data-label="Name">
                            {dealership.name}
                            {dealership.demoMode && (
                              <span style={{ marginLeft: '8px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: '4px', padding: '1px 6px', fontSize: '10px', fontWeight: '700', color: 'rgba(147,197,253,1)', letterSpacing: '0.05em' }}>DEMO</span>
                            )}
                          </td>
                          <td data-label="Location">{dealership.location || '-'}</td>
                          <td data-label="Status">
                            <span className={`status-badge ${dealership.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                              {dealership.status}
                            </span>
                          </td>
                          <td data-label="Actions">
                            <div className="action-buttons">
                            <button
                              type="button"
                              className="btn-edit-small"
                              onClick={() => handleAddDealershipToOwner(dealership)}
                              disabled={dealershipActionId === dealership.id}
                              title="Edit dealership"
                            >
                              Edit
                            </button>
                            {isSuperAdmin && (
                              <button
                                type="button"
                                className="btn-copy-small"
                                onClick={() => setCopyModal({ isOpen: true, dealership, targetOwnerId: '', newName: `${dealership.name} (Copy)`, move: false })}
                                disabled={dealershipActionId === dealership.id}
                                title="Copy to another owner or duplicate"
                              >
                                Copy
                              </button>
                            )}
                            {dealership.status === 'ACTIVE' ? (
                              <button
                                type="button"
                                className="btn-deactivate-small"
                                onClick={() => handleToggleDealershipStatus(dealership.id, dealership.status)}
                                disabled={dealershipActionId === dealership.id}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn-activate-small"
                                onClick={() => handleToggleDealershipStatus(dealership.id, dealership.status)}
                                disabled={dealershipActionId === dealership.id}
                              >
                                Activate
                              </button>
                            )}
                            {dealership.status === 'INACTIVE' && isSuperAdmin && (
                              <button
                                type="button"
                                className="btn-deactivate-small"
                                onClick={() => handleDeleteDealership(dealership.id)}
                                disabled={dealershipActionId === dealership.id}
                              >
                                Delete
                              </button>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <div className="no-dealerships-message">
                    <p>No dealerships yet. Click "Add Dealership" to create one.</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowEditOwnerModal(false)}
                  disabled={loading}
                >
                  Close
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Owner Info'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'talonFields' && isSuperAdmin && (
        <div className="admin-owners">
          {loading ? (
            <div className="loading">Loading dealerships...</div>
          ) : (
            <TalonFieldMapping owners={owners} />
          )}
        </div>
      )}

      {activeTab === 'templates' && isPlatformManager && (
        <div className="admin-section">
          <h2>Dropdown Templates</h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: '-6px', marginBottom: '18px' }}>
            Edit the dropdown options new dealerships inherit. Use “Push to linked dealerships” to add new
            options (e.g. this year’s models) to every store using a template—existing and custom options are
            never removed, and fit specialists are never touched.
          </p>
          <TemplateManager />
        </div>
      )}

      {activeTab === 'support' && isSuperAdmin && (
        <div className="admin-section">
          <h2>Support Form</h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: '-6px', marginBottom: '18px' }}>
            Messages submitted from the public support page (masters-path.com/support—the App Store
            support URL) are emailed to the address below. Replies go directly to the person who wrote in.
          </p>
          <label style={{ display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
            Support recipient email
          </label>
          <input
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            placeholder={supportEffective || 'support@example.com'}
            style={{
              width: '100%', maxWidth: '420px', boxSizing: 'border-box', padding: '11px 14px', marginTop: '6px',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: '8px', color: '#fff', fontSize: '14px',
            }}
          />
          {supportEffective && (
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '8px' }}>
              Currently sending to: <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{supportEffective}</strong>
            </p>
          )}
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn-add-owner" onClick={saveSupportEmail} disabled={supportSaving}>
              {supportSaving ? 'Saving…' : 'Save'}
            </button>
            {supportMsg && (
              <span style={{ fontSize: '13px', color: supportMsg === 'Saved.' ? '#5bd99a' : '#ff7a7a' }}>{supportMsg}</span>
            )}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '28px 0' }} />

          <h2>Account Requests</h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', marginTop: '-6px', marginBottom: '18px' }}>
            Submissions from the public "Request an account" page (masters-path.com/request-account, linked
            from the login screen) are emailed to the address below. Replies go directly to the requester.
          </p>
          <label style={{ display: 'block', fontSize: '13px', color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
            Account-request recipient email
          </label>
          <input
            type="email"
            value={acctEmail}
            onChange={(e) => setAcctEmail(e.target.value)}
            placeholder={acctEffective || 'sales@example.com'}
            style={{
              width: '100%', maxWidth: '420px', boxSizing: 'border-box', padding: '11px 14px', marginTop: '6px',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: '8px', color: '#fff', fontSize: '14px',
            }}
          />
          {acctEffective && (
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', marginTop: '8px' }}>
              Currently sending to: <strong style={{ color: 'rgba(255,255,255,0.7)' }}>{acctEffective}</strong>
            </p>
          )}
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button className="btn-add-owner" onClick={saveAccountRequestEmail} disabled={acctSaving}>
              {acctSaving ? 'Saving…' : 'Save'}
            </button>
            {acctMsg && (
              <span style={{ fontSize: '13px', color: acctMsg === 'Saved.' ? '#5bd99a' : '#ff7a7a' }}>{acctMsg}</span>
            )}
          </div>
        </div>
      )}

      {/* Branded Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText || 'Confirm'}
        confirmStyle={confirmModal.style}
      />

      {/* Branded Prompt Modal */}
      <PromptModal
        isOpen={promptModal.isOpen}
        onClose={() => {
          setPromptModal({ isOpen: false, title: '', message: '', placeholder: '', onSubmit: null });
        }}
        onSubmit={promptModal.onSubmit}
        title={promptModal.title}
        message={promptModal.message}
        placeholder={promptModal.placeholder}
      />

      {/* Info Modal (branded) */}
      <ConfirmModal
        isOpen={infoModal.isOpen}
        onClose={() => setInfoModal({ ...infoModal, isOpen: false })}
        onConfirm={() => setInfoModal({ ...infoModal, isOpen: false })}
        title={infoModal.title || 'Notice'}
        message={infoModal.message}
        confirmText="OK"
        confirmStyle="primary"
        hideCancel
      />

      {/* Copy / Move Dealership Modal */}
      {copyModal.isOpen && copyModal.dealership && (
        <div className="modal-overlay" onClick={() => setCopyModal({ isOpen: false, dealership: null, targetOwnerId: '', newName: '', move: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{copyModal.move ? 'Move Dealership' : 'Copy Dealership'}</h2>
              <button className="close-btn" onClick={() => setCopyModal({ isOpen: false, dealership: null, targetOwnerId: '', newName: '', move: false })}>×</button>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ marginBottom: '20px', color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.5' }}>
                {copyModal.move
                  ? <>Move <strong style={{ color: '#ffffff' }}>{copyModal.dealership.name}</strong> to a new owner. All data stays with the dealership and the original is removed from this owner.</>
                  : <>Copy <strong style={{ color: '#ffffff' }}>{copyModal.dealership.name}</strong>—all data, targets, users, and history will be duplicated to the new dealership.</>
                }
              </p>

              {!copyModal.move && (
                <div className="form-group">
                  <label>New Dealership Name *</label>
                  <input
                    type="text"
                    value={copyModal.newName}
                    onChange={(e) => setCopyModal({ ...copyModal, newName: e.target.value })}
                    placeholder="Enter a name for the new dealership"
                  />
                </div>
              )}

              <div className="form-group">
                <label>{copyModal.move ? 'Move to Owner *' : 'Assign Copy to Owner *'}</label>
                <select
                  value={copyModal.targetOwnerId}
                  onChange={(e) => setCopyModal({ ...copyModal, targetOwnerId: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#ffffff', fontSize: '14px', fontFamily: 'inherit' }}
                >
                  <option value="" style={{ background: '#1e1e1e', color: '#ffffff' }}>Select an owner...</option>
                  {owners.map(o => (
                    <option key={o.id} value={o.id} style={{ background: '#1e1e1e', color: '#ffffff' }}>
                      {o.name} ({o.email}){o.id === editingOwner?.id ? '—same owner' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '8px', padding: '14px', borderRadius: '8px', background: copyModal.move ? 'rgba(220,53,69,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${copyModal.move ? 'rgba(220,53,69,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                <input
                  type="checkbox"
                  id="move-checkbox"
                  checked={copyModal.move}
                  onChange={(e) => setCopyModal({ ...copyModal, move: e.target.checked })}
                  disabled={copyModal.targetOwnerId === editingOwner?.id}
                  style={{ marginTop: '2px', cursor: copyModal.targetOwnerId === editingOwner?.id ? 'not-allowed' : 'pointer', accentColor: '#dc3545', width: '16px', height: '16px', flexShrink: 0 }}
                />
                <label htmlFor="move-checkbox" style={{ color: copyModal.move ? '#ff6b6b' : 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: '1.4', cursor: copyModal.targetOwnerId === editingOwner?.id ? 'not-allowed' : 'pointer' }}>
                  <strong style={{ display: 'block', marginBottom: '2px', color: copyModal.move ? '#ff6b6b' : 'rgba(255,255,255,0.9)' }}>Move instead of copy</strong>
                  {copyModal.targetOwnerId === editingOwner?.id
                    ? 'Move is not available when copying to the same owner.'
                    : 'The dealership will be removed from this owner and reassigned. No duplication occurs.'}
                </label>
              </div>
            </div>

            <div className="modal-actions" style={{ padding: '0 24px 24px' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setCopyModal({ isOpen: false, dealership: null, targetOwnerId: '', newName: '', move: false })}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={copyModal.move ? 'btn-deactivate' : 'btn-submit'}
                onClick={handleCopyDealership}
                disabled={loading || !copyModal.targetOwnerId || (!copyModal.move && !copyModal.newName.trim())}
              >
                {loading
                  ? (copyModal.move ? 'Moving...' : 'Copying...')
                  : (copyModal.move ? 'Move Dealership' : 'Copy Dealership')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dealership Form Modal */}
      {dealershipFormModal.isOpen && editingOwner && (
        <div className="modal-overlay" onClick={() => setDealershipFormModal({ isOpen: false, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75 })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{dealershipFormModal.editingId ? 'Edit Dealership' : 'Add New Dealership'}</h2>
              <button className="close-btn" onClick={() => setDealershipFormModal({ isOpen: false, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75 })}>×</button>
            </div>
            <form onSubmit={handleSaveDealership}>
              <div className="form-section">
                <div className="form-group">
                  <label>Dealership Name *</label>
                  <input
                    type="text"
                    value={dealershipFormModal.name}
                    onChange={(e) => setDealershipFormModal({...dealershipFormModal, name: e.target.value})}
                    required
                    placeholder="e.g., Downtown Harley"
                  />
                </div>
                <div className="form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    value={dealershipFormModal.location}
                    onChange={(e) => setDealershipFormModal({...dealershipFormModal, location: e.target.value})}
                    required
                    placeholder="e.g., New York, NY"
                  />
                </div>

                {!dealershipFormModal.editingId && (
                  <div className="form-group">
                    <label>Dropdown Template</label>
                    <select
                      value={dealershipFormModal.templateId || ''}
                      onChange={(e) => setDealershipFormModal({ ...dealershipFormModal, templateId: e.target.value })}
                    >
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                      <option value="">None (blank dealership)</option>
                    </select>
                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '6px' }}>
                      Pre-fills lenders, F&amp;I, motorcycles, sources, deal classes &amp; statuses. Fit specialists are always added per-dealership.
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={dealershipFormModal.demoMode}
                      onChange={(e) => setDealershipFormModal({ ...dealershipFormModal, demoMode: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#8b5cf6' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', color: 'rgba(255,255,255,0.95)' }}>Demo Dealership</div>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '3px' }}>
                        Shows realistic sample data instead of real data
                      </div>
                    </div>
                  </label>

                  {dealershipFormModal.demoMode && (
                    <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: '8px' }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px', color: 'rgba(255,255,255,0.9)' }}>
                        Target Achievement Rate: {Math.round(dealershipFormModal.achievementRate * 100)}%
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginBottom: '12px' }}>
                        Controls how the demo dealership performs relative to targets
                      </div>
                      <input
                        type="range"
                        min="50"
                        max="100"
                        step="5"
                        value={dealershipFormModal.achievementRate * 100}
                        onChange={(e) => setDealershipFormModal({ ...dealershipFormModal, achievementRate: Number(e.target.value) / 100 })}
                        style={{ width: '100%', accentColor: '#8b5cf6' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '6px' }}>
                        <span>50% (Struggling)</span>
                        <span>75% (Average)</span>
                        <span>100% (Excellent)</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setDealershipFormModal({ isOpen: false, editingId: null, name: '', location: '', demoMode: false, achievementRate: 0.75 })}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={loading || !dealershipFormModal?.name?.trim() || !dealershipFormModal?.location?.trim()}
                >
                  {loading ? (dealershipFormModal?.editingId ? 'Updating...' : 'Creating...') : (dealershipFormModal?.editingId ? 'Update Dealership' : 'Create Dealership')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
